import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Container,
  CircularProgress
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../context/authContext';
import OrganizationSetup from '../components/signup/OrganizationSetup';
import PlanSelection from '../components/signup/PlanSelection';
import PlanConfirmation from '../components/signup/PlanConfirmation';
import styles from '../styles/OrganizationSignup.module.css';

const OrganizationSignup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithRedirect, isLoading: auth0Loading, isAuthenticated } = useAuth0();
  const { authenticated } = useAuth();

  // Get initial plan from navigation state OR sessionStorage (from Auth0 callback)
  const getInitialPlanData = () => {
    // Priority 1: Check navigation state (direct navigation from Plans page)
    if (location.state?.selectedPlan) {
      return {
        plan: location.state.selectedPlan,
        interval: location.state.billingInterval || 'month',
        priceId: location.state.priceId
      };
    }

    // Priority 2: Check sessionStorage (after Auth0 redirect)
    const storedPlanData = sessionStorage.getItem('organizationPlanData');
    if (storedPlanData) {
      try {
        const planData = JSON.parse(storedPlanData);

        // Clear sessionStorage after reading
        sessionStorage.removeItem('organizationPlanData');

        // Convert stored plan data to plan object format
        return {
          plan: {
            id: planData.planId,
            name: planData.planName,
            prices: planData.priceId ? [{
              id: planData.priceId,
              unit_amount: planData.amount,
              recurring: { interval: planData.billingInterval }
            }] : []
          },
          interval: planData.billingInterval,
          priceId: planData.priceId
        };
      } catch (error) {
      }
    }

    // No plan data found
    return { plan: null, interval: 'month', priceId: null };
  };

  const initialPlanData = getInitialPlanData();

  // If plan is pre-selected, skip to step 0 (Organization Setup), user will skip plan selection
  const [activeStep, setActiveStep] = useState(0);
  const [organizationData, setOrganizationData] = useState({
    name: '',
    identifier: '',
    type: 'small_business',
    teamSize: '1-5'
  });
  const [selectedPlan, setSelectedPlan] = useState(initialPlanData.plan);
  const [billingInterval, setBillingInterval] = useState(initialPlanData.interval);

  // Log initial plan data for debugging
  React.useEffect(() => {
    if (initialPlanData.plan) {
    }
  }, []);

  const steps = [
    'Organization Details',
    'Select Plan',
    'Review & Confirm',
    'Payment'
  ];

  // Redirect to login if not authenticated
  React.useEffect(() => {
    // Wait for Auth0 to finish loading before checking authentication
    if (!auth0Loading && !isAuthenticated) {
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/home`,
          // Custom parameter that Auth0 Action can read from event.request.query
          signup_type: 'organization'
        },
        appState: {
          returnTo: '/signup/organization',
          intent: 'create_organization'
        }
      });
    }
  }, [auth0Loading, isAuthenticated, loginWithRedirect]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleOrganizationSubmit = (data) => {
    setOrganizationData((prev) => ({ ...prev, ...data }));
    handleNext();
  };

  const handlePlanSelect = (plan, interval) => {
    setSelectedPlan(plan);
    setBillingInterval(interval);
    handleNext();
  };

  const handleConfirm = () => {
    // This will trigger the payment step
    handleNext();
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <OrganizationSetup
            initialData={organizationData}
            onSubmit={handleOrganizationSubmit}
            onCancel={() => navigate('/plans')}
          />
        );
      case 1:
        return (
          <PlanSelection
            initialPlan={selectedPlan}
            initialInterval={billingInterval}
            teamSize={organizationData.teamSize}
            onSelect={handlePlanSelect}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <PlanConfirmation
            organizationData={organizationData}
            selectedPlan={selectedPlan}
            billingInterval={billingInterval}
            onConfirm={handleConfirm}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <div className={styles['payment-step']}>
            <p className={styles['payment-message']}>
              Redirecting to secure payment...
            </p>
          </div>
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  // Show loading while Auth0 is checking authentication
  if (auth0Loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={styles['signup-container']}>
      <Container maxWidth="md">
        <header className={styles['signup-header']}>
          <h1 className={styles['signup-title']}>
            Create Your Organization
          </h1>
          <p className={styles['signup-description']}>
            Set up your team workspace and start collaborating
          </p>
        </header>

        <Box className={styles['stepper-wrapper']}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            className={styles['stepper']}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    classes: {
                      root: styles['step-icon'],
                      active: styles['step-icon-active'],
                      completed: styles['step-icon-completed']
                    }
                  }}
                >
                  <span className={styles['step-label']}>
                    {label}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          className={styles['step-content']}
          role="region"
          aria-label={`Step ${activeStep + 1} of ${steps.length}: ${steps[activeStep]}`}
        >
          {getStepContent(activeStep)}
        </Box>
      </Container>
    </div>
  );
};

export default OrganizationSignup;
